<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useReviewReportStore } from '@/store';
import ReviewReportTrackTotal from './ReviewReportTrackTotal.vue';
import ReviewReportTrackMore from './ReviewReportTrackMore.vue';

const reviewReportStore = useReviewReportStore();
const { track } = storeToRefs(reviewReportStore);

const baseTotals = ref({
  total: 0,
  disciplines: [],
});

const showMore = ref(false);

const showMoreText = computed(() => {
  return showMore.value
    ? {
        icon: 'expand_less',
        text: 'Show Less',
      }
    : {
        icon: 'expand_more',
        text: 'Show More',
      };
});

const isLoading = computed(() => track.value.loading || !Object.keys(track.value.data).length);

const eventsScheduled = computed(() => defineTotals('events_scheduled'));
const eventsCompleted = computed(() => defineTotals('events_completed'));
const evaluationsCompleted = computed(() => defineTotals('evaluations_completed'));

const defineTotals = (key: string) => {
  if (isLoading.value) return baseTotals.value;
  return track.value.data[key];
};

const onShowMore = () => (showMore.value = !showMore.value);
</script>

<template>
  <div class="mt-3 flex flex-wrap p-3 mb-4 gap-3 border-2 border-gray-100 bg-white border-round-xl relative">
    <div class="flex-1">
      <ReviewReportTrackTotal
        title="Events Scheduled"
        tooltip="Sessions, Consultations, and Evaluations scheduled in the selected date range."
        icon="calendar_today"
        :total="eventsScheduled.total"
        color="blue"
      />
      <ReviewReportTrackMore :disciplines="eventsScheduled.disciplines" color="blue" :show-more="showMore" />
    </div>
    <div class="flex-1">
      <ReviewReportTrackTotal
        title="Events Completed"
        tooltip="Sessions, Consultations, and Evaluations completed in the selected date range."
        icon="perm_contact_calendar"
        :total="eventsCompleted.total"
        color="purple"
      />
      <ReviewReportTrackMore :disciplines="eventsCompleted.disciplines" color="purple" :show-more="showMore" />
    </div>
    <div class="flex-1">
      <ReviewReportTrackTotal
        title="Evaluations Completed"
        tooltip="Evaluations where the student has been documented present."
        icon="assignment_turned_in"
        :total="evaluationsCompleted.total"
        color="pink"
      />
      <ReviewReportTrackMore :disciplines="evaluationsCompleted.disciplines" color="pink" :show-more="showMore" />
    </div>
    <AppButton
      type="text"
      class="font-normal see-more"
      :text="showMoreText.text"
      :icon-left="showMoreText.icon"
      @click="onShowMore"
    />
  </div>
</template>

<style lang="scss" scoped>
.see-more {
  position: absolute !important;
  right: 1rem;
  bottom: -18px;
}
</style>
